import _ from "lodash";
import { useCallback, useReducer } from "react";
import { ViewOptionStateEnum } from "../../contextual-app-bar/ViewOptionStateEnum";
import { RecipientTypeEnum } from "../../../common/enums/RecipientTypeEnum";

export enum AlarmSetpointNotificationTabEnum {
    None = -1,
    Recipients = 0,
    NotificationLists = 1
}

export enum NotificationTypeEnum {
    OneTime = 0,
    Tiered = 1
}

export type AlarmSetpointNotificationStateType = {
    activeTab: AlarmSetpointNotificationTabEnum;
    recipientFilterText: string;
    notificationListFilterText: string;
    alarmSchemeFilterText: string;
    viewSelected: boolean;
    notificationType: NotificationTypeEnum;
    viewOptionState: ViewOptionStateEnum;
    recipientType: RecipientTypeEnum;
    organizationID: number | undefined;
    defaultOrganizationID: number | undefined;
};

enum AlarmSetpointNotificationActionType {
    ActiveTabChanged = "ACTIVE_TAB_CHANGED",
    RecipientFilterChanged = "RECIPIENT_FILTER_CHANGED",
    NotificationListFilterChanged = "NOTIFICATIONLIST_FILTER_CHANGED",
    AlarmSchemeFilterChanged = "ALARMSCHEME_FILTER_CHANGED",
    RecipientTypeChanged = "RECIPIENT_TYPE_CHANGED",
    ViewSelectedChanged = "VIEW_SELECTED_CHANGED",
    NotificationTypeChanged = "NOTIFY_TYPE_CHANGED",
    OrganizationChanged = 'ORGANIZATION_CHANGED',
    ResetChanged = "RESET_CHANGED",
}

type AlarmSetpointNotificationAction = {
    type: AlarmSetpointNotificationActionType.ActiveTabChanged;
    activeTab: AlarmSetpointNotificationTabEnum;
} | {
     type: AlarmSetpointNotificationActionType.RecipientFilterChanged;
     payload: string;
} | {
    type: AlarmSetpointNotificationActionType.NotificationListFilterChanged;
    payload: string;
} | {
    type: AlarmSetpointNotificationActionType.AlarmSchemeFilterChanged;
    payload: string;
} | {
    type: AlarmSetpointNotificationActionType.NotificationTypeChanged;
    payload: NotificationTypeEnum;
} | {
    type: AlarmSetpointNotificationActionType.RecipientTypeChanged;
    payload: RecipientTypeEnum;
} | {
    type: AlarmSetpointNotificationActionType.ViewSelectedChanged;
    payload: boolean;
} | {
    type: AlarmSetpointNotificationActionType.OrganizationChanged;
    payload: number | undefined;
} | {
    type: AlarmSetpointNotificationActionType.ResetChanged;
    payload: void;
};

const initialState: AlarmSetpointNotificationStateType = {
    activeTab: AlarmSetpointNotificationTabEnum.Recipients,
    recipientFilterText: '',
    notificationListFilterText: '',
    alarmSchemeFilterText: '',
    viewSelected: false,
    viewOptionState: ViewOptionStateEnum.Default,
    notificationType: NotificationTypeEnum.OneTime,
    recipientType: RecipientTypeEnum.Invalid,
    organizationID: undefined,
    defaultOrganizationID: undefined
};

const optionsChanged = (newState: AlarmSetpointNotificationStateType, 
    defaultState: AlarmSetpointNotificationStateType) => 
    newState.recipientFilterText !== defaultState.recipientFilterText ||
    newState.alarmSchemeFilterText !== defaultState.alarmSchemeFilterText ||
    newState.notificationListFilterText !== defaultState.notificationListFilterText ||
    newState.notificationType !== defaultState.notificationType ||
    newState.viewSelected !== defaultState.viewSelected ||
    newState.recipientType !== defaultState.recipientType ||
    newState.organizationID !== newState.defaultOrganizationID;

function setpointNotificationReducer(state: AlarmSetpointNotificationStateType, action: AlarmSetpointNotificationAction): AlarmSetpointNotificationStateType {
    let copy = _.cloneDeep(state);    

    switch (action.type) {

        case AlarmSetpointNotificationActionType.ActiveTabChanged:
            copy.activeTab = action.activeTab;
            break;

        case AlarmSetpointNotificationActionType.RecipientFilterChanged:
            copy.recipientFilterText = action.payload;
            break;

        case AlarmSetpointNotificationActionType.NotificationListFilterChanged:
            copy.notificationListFilterText = action.payload;
            break;
    
        case AlarmSetpointNotificationActionType.AlarmSchemeFilterChanged:
            copy.alarmSchemeFilterText = action.payload;
            break;
    
        case AlarmSetpointNotificationActionType.ViewSelectedChanged:
            copy.viewSelected = action.payload;
            break;

        case AlarmSetpointNotificationActionType.NotificationTypeChanged:
            copy.notificationType = action.payload;
            break;

        case AlarmSetpointNotificationActionType.RecipientTypeChanged:
            copy.recipientType = action.payload;
            break;

        case AlarmSetpointNotificationActionType.OrganizationChanged:
            copy.organizationID = action.payload;
            if (copy.defaultOrganizationID === undefined) {
                copy.defaultOrganizationID = action.payload;
            }
            break;
                
        case AlarmSetpointNotificationActionType.ResetChanged:
            copy = _.cloneDeep(initialState);
            break;

        default:
            return state;
    }

    copy.viewOptionState = optionsChanged(copy, initialState) ? 
                            ViewOptionStateEnum.Changed : 
                            ViewOptionStateEnum.Default;

    return copy;
}

export type UseAlarmSetpointNotificationStateType = {
    state: AlarmSetpointNotificationStateType;
    setActiveTab: (activeTab: AlarmSetpointNotificationTabEnum) => void;
    setRecipientFilterText: (value: string) => void;
    setRecipientType: (value: RecipientTypeEnum) => void;
    setNotificationListFilterText: (value: string) => void;
    setAlarmSchemeFilterText: (value: string) => void;
    setViewSelected: (value: boolean) => void;
    setNotificationType: (value: NotificationTypeEnum) => void;
    setOrganization: (value: number | undefined) => void;
    resetState: () => void;
};

export function useAlarmSetpointNotificationState(): UseAlarmSetpointNotificationStateType {

    const [state, dispatch] = useReducer(setpointNotificationReducer, initialState);

    const setActiveTab = useCallback((activeTab: AlarmSetpointNotificationTabEnum) => dispatch({
        type: AlarmSetpointNotificationActionType.ActiveTabChanged,
        activeTab: activeTab
    }), []);

    const setRecipientFilterText = useCallback((value: string) => dispatch({
        type: AlarmSetpointNotificationActionType.RecipientFilterChanged,
        payload: value
    }), []);

    const setNotificationListFilterText = useCallback((value: string) => dispatch({
        type: AlarmSetpointNotificationActionType.NotificationListFilterChanged,
        payload: value
    }), []);

    const setAlarmSchemeFilterText = useCallback((value: string) => dispatch({
        type: AlarmSetpointNotificationActionType.AlarmSchemeFilterChanged,
        payload: value
    }), []);

    const setViewSelected = useCallback((value: boolean) => dispatch({
        type: AlarmSetpointNotificationActionType.ViewSelectedChanged,
        payload: value
    }), []);

    const setNotificationType = useCallback((value: NotificationTypeEnum) => dispatch({
        type: AlarmSetpointNotificationActionType.NotificationTypeChanged,
        payload: value
    }), []);

    const setRecipientType = useCallback((value: RecipientTypeEnum) => dispatch({
        type: AlarmSetpointNotificationActionType.RecipientTypeChanged,
        payload: value
    }), []);

    const setOrganization = useCallback((value: number | undefined) => dispatch({
        type: AlarmSetpointNotificationActionType.OrganizationChanged,
        payload: value
    }), []);

    const resetState = useCallback(() => dispatch({
        type: AlarmSetpointNotificationActionType.ResetChanged,
        payload: undefined
    }), [])

    return {
        state,
        setActiveTab,
        setRecipientFilterText,
        setRecipientType,
        setNotificationListFilterText,
        setAlarmSchemeFilterText,
        setViewSelected,
        setNotificationType,
        setOrganization,
        resetState
    };
}
